// Fixed-position install pill that sits in the top-right of every page.
// Renders nothing once the app is already installed (or when the browser
// hasn't surfaced an install prompt yet — e.g. iOS Safari).
//
// The button is intentionally framework-agnostic — no MUI, no portals — so
// it can sit cleanly on top of the marketing site, the login screen, the
// setup wizard and the management dashboard without style conflicts.

import React, { useState } from "react";
import { usePWAInstall } from "../hooks/usePWAInstall";

export default function InstallButton() {
  const { canInstall, isInstalled, promptInstall } = usePWAInstall();
  const [busy, setBusy] = useState(false);

  if (isInstalled || !canInstall) return null;

  const handleClick = async () => {
    setBusy(true);
    try { await promptInstall(); }
    finally { setBusy(false); }
  };

  return (
    <>
      <style>{`
        .pwa-install-btn {
          position: fixed;
          top: 18px;
          right: 18px;
          z-index: 5000;
          display: inline-flex;
          align-items: center;
          gap: 9px;
          padding: 10px 16px 10px 13px;
          background: linear-gradient(135deg, #c8a05a 0%, #a8814a 100%);
          color: #1a110a;
          border: 1px solid rgba(255,255,255,.18);
          border-radius: 999px;
          font-family: 'Jost', system-ui, sans-serif;
          font-size: 12px;
          font-weight: 500;
          letter-spacing: .12em;
          text-transform: uppercase;
          cursor: pointer;
          box-shadow:
            0 4px 18px rgba(40,20,5,.28),
            0 0 0 1px rgba(200,160,90,.35),
            inset 0 1px 0 rgba(255,255,255,.25);
          transition: transform .18s ease, box-shadow .18s ease, filter .18s ease;
        }
        .pwa-install-btn:hover:not(:disabled) {
          transform: translateY(-1px) scale(1.02);
          filter: brightness(1.04);
          box-shadow:
            0 6px 22px rgba(40,20,5,.36),
            0 0 0 1px rgba(200,160,90,.5),
            inset 0 1px 0 rgba(255,255,255,.3);
        }
        .pwa-install-btn:active:not(:disabled) { transform: translateY(0) scale(.99); }
        .pwa-install-btn:disabled { opacity: .65; cursor: progress; }
        .pwa-install-btn svg { flex-shrink: 0; }
        .pwa-install-pulse {
          position: absolute;
          inset: -2px;
          border-radius: 999px;
          border: 1.5px solid rgba(200,160,90,.55);
          animation: pwa-pulse 2.2s ease-out infinite;
          pointer-events: none;
        }
        @keyframes pwa-pulse {
          0%   { transform: scale(1);    opacity: .8; }
          100% { transform: scale(1.18); opacity: 0;  }
        }
        @media (max-width: 480px) {
          .pwa-install-btn { top: 12px; right: 12px; padding: 8px 14px 8px 11px; font-size: 11px; }
        }
      `}</style>

      <button
        type="button"
        className="pwa-install-btn"
        onClick={handleClick}
        disabled={busy}
        title="Install MyHotel as an app on this device"
        aria-label="Install MyHotel"
      >
        <span className="pwa-install-pulse" aria-hidden="true" />
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        <span>{busy ? "Installing…" : "Install App"}</span>
      </button>
    </>
  );
}
